'use client';

import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useInfiniteHits, useInstantSearch, useSearchBox } from 'react-instantsearch';
import scrollToTopOfProductGrid from 'components/consumer/ProductCardGridUI/helpers/scrollToTopOfProductGrid';
import useInfiniteStaticProducts from 'hooks/useInfiniteStaticProducts';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import ProductCardStaticPlaceholderGroup from 'components/consumer/ProductCardStaticPlaceholderGroup';
import useMobileColumnCount from 'components/consumer/ProductListingPage/state/mobileColumnCount/useMobileColumnCount';
import { useDeferredValue } from '../../hooks/reactShims';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';
import AdvancedSearchProductsGrid from '../AdvancedSearchProductsGrid';
import LoadMoreButton from '../AdvancedSearchProductsGrid/LoadMoreButton';
import { getVariantsByMatchedColorFirst } from './utils';

/** Delay (ms) before showing "No results found" to avoid flash while Algolia is still loading */
const NO_RESULTS_DELAY_MS = 750;
const AdvancedSearchProductHits = () => {
  const {
    items,
    showMore,
    isLastPage
  } = useInfiniteHits();
  const {
    results,
    status,
    setIndexUiState,
    indexUiState
  } = useInstantSearch();
  const {
    query: searchBoxQuery
  } = useSearchBox();
  const mobileColumnCount = useMobileColumnCount();
  const {
    isReadyToShipActive,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  const isFirstMountRef = useRef(true);
  const previousProductsRef = useRef([]);
  const previousFilterStateKeyRef = useRef(null);

  // Track if a complete search has been done (to avoid showing "No results" during initialization)
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const [isFilterTransitioning, setIsFilterTransitioning] = useState(false);
  // Delay showing "No results" so we don't flash it during brief loading gaps
  const [showNoResults, setShowNoResults] = useState(false);

  // Create a serialized key that captures all filter state (both Algolia refinements
  // and context-based filters) so we can detect any filter change
  const filterStateKey = useMemo(() => JSON.stringify({
    refinementList: indexUiState?.refinementList || {},
    isReadyToShipActive,
    minWidth,
    maxWidth
  }), [indexUiState?.refinementList, isReadyToShipActive, minWidth, maxWidth]);

  // When any filter changes, reset pagination to page 1, scroll to top, and indicate transition
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      previousFilterStateKeyRef.current = filterStateKey;
      return;
    }

    // Detect if filter state has changed
    if (filterStateKey !== previousFilterStateKeyRef.current) {
      setIsFilterTransitioning(true);
      previousFilterStateKeyRef.current = filterStateKey;
      setIndexUiState(prev => ({
        ...prev,
        page: 1
      }));
      // Pass false to avoid jarring focus shift when clicking filter checkboxes
      scrollToTopOfProductGrid(false);
    }
  }, [filterStateKey, setIndexUiState]);

  // Clear filter transition state once new results have arrived (or when response is 0 hits)
  useEffect(() => {
    if (!isFilterTransitioning) return;
    if (items?.length > 0) {
      setIsFilterTransitioning(false);
    } else if (status === 'idle' && items !== undefined) {
      // Algolia returned with 0 hits after filter change; stop showing skeleton
      setIsFilterTransitioning(false);
    }
  }, [isFilterTransitioning, items, status]);

  // Track when search has completed (mark once Algolia returns any result set, even empty)
  useEffect(() => {
    if (status === 'idle' && !hasSearchCompleted && items !== undefined) {
      setHasSearchCompleted(true);
    }
  }, [status, hasSearchCompleted, items]);

  // Delay showing "No results found" to avoid brief flash while Algolia is still loading
  const isSearchPending = status === 'loading' || status === 'stalled';
  useEffect(() => {
    if (items?.length > 0 || isSearchPending) {
      setShowNoResults(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setShowNoResults(true);
    }, NO_RESULTS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [items?.length, isSearchPending]);

  // Prefer SearchBox query – results.query can be stale/empty when filters change (e.g. ready to ship)
  const searchKey = useMemo(() => {
    const query = searchBoxQuery || results?.query || '';
    const params = results?.params || '';
    const paramsWithoutPage = params.split('&').filter(param => !param.startsWith('page=')).join('&');
    return `${query}-${paramsWithoutPage}`;
  }, [searchBoxQuery, results?.query, results?.params]);
  const productIdsRaw = useMemo(() => {
    if (!items?.length) {
      return [];
    }
    return items.map(hit => {
      const id = hit.product_id;
      return typeof id === 'string' ? parseInt(id, 10) : id;
    }).filter(id_0 => id_0 > 0 && !Number.isNaN(id_0));
  }, [items]);

  // Defer product IDs so we don't trigger immediate re-fetches while typing
  const productIds = useDeferredValue(productIdsRaw);
  const {
    products: productsWithVariantsToShow,
    loading
  } = useInfiniteStaticProducts({
    productIds,
    fetchLimit: productIds?.length,
    searchKey
  }, {
    skipSorting: true
  });
  const productsWithHighlightedVariantsFirst = useMemo(() => productsWithVariantsToShow.map(product => ({
    ...product,
    variantsToShow: getVariantsByMatchedColorFirst(product, items)
  })), [productsWithVariantsToShow, items]);
  const productsInAlgoliaOrder = useMemo(() => {
    if (!items?.length || !productsWithHighlightedVariantsFirst?.length) {
      return productsWithHighlightedVariantsFirst ?? [];
    }
    const productsByIdMap = new Map();
    productsWithHighlightedVariantsFirst.forEach(product_0 => {
      const idNormalized = String(product_0.id);
      if (!productsByIdMap.has(idNormalized)) {
        productsByIdMap.set(idNormalized, product_0);
      }
    });
    return items.map(({
      __position,
      __queryID,
      is_in_stock,
      objectID,
      product_id: productIdFromAlgolia
    }) => {
      const productIdNormalized = String(productIdFromAlgolia);
      const productIdNumber = parseInt(productIdFromAlgolia, 10);
      const product_1 = productsByIdMap.get(productIdNormalized);
      if (!product_1) {
        return null;
      }
      const isInStock = is_in_stock === true;
      const variantsWithQuickship = (product_1.variantsToShow || []).map(variantData => ({
        ...variantData,
        quickship: isInStock
      }));
      const variantsWithColor = variantsWithQuickship.filter(variant => !!variant?.color);
      // Keep at least one variant so we always show price
      const variantsToShow = variantsWithColor.length > 0 ? variantsWithColor : variantsWithQuickship.slice(0, 1);
      return {
        ...product_1,
        quickship: isInStock,
        variants: (product_1.variants || []).map(variantData_0 => ({
          ...variantData_0,
          quickship: isInStock
        })),
        variantsToShow,
        algoliaMetaData: {
          queryID: __queryID ?? '',
          position: __position ?? 0,
          objectID: objectID ?? productIdNumber ?? ''
        }
      };
    }).filter(Boolean);
  }, [items, productsWithHighlightedVariantsFirst]);

  // Keep previous products visible until new ones are ready (prevents flash/blink).
  // When Algolia returns 0 hits, show nothing (no results) – don't keep old products.
  const displayProducts = useMemo(() => {
    if (!items?.length) {
      previousProductsRef.current = [];
      return [];
    }
    if (productsInAlgoliaOrder?.length > 0) {
      previousProductsRef.current = productsInAlgoliaOrder;
      return productsInAlgoliaOrder;
    }
    // While loading new results, show previous products
    return previousProductsRef.current;
  }, [items?.length, productsInAlgoliaOrder]);

  // Detect if we're in a "refreshing" state (stale data shown)
  const isRefreshing = status === 'loading' || status === 'stalled' || loading;
  if (!items?.length) {
    // Show loading skeleton if: Algolia loading, filter transitioning, search not completed yet, or within delay before "No results"
    const showSkeleton = status === 'loading' || status === 'stalled' || isFilterTransitioning || !hasSearchCompleted || !showNoResults;
    if (showSkeleton) {
      return <div className="md:space-y-8 md:mb-8 md:flex-1 min-h-[400px]">
          <ProductCardGridUI limitWidth={false} columnsOnDesktop="auto" columnsOnMobile={mobileColumnCount}>
            <ProductCardStaticPlaceholderGroup count={NUMBER_OF_PRODUCTS_TO_SHOW} />
          </ProductCardGridUI>
        </div>;
    }
    return <div role="status" aria-live="polite" className="py-8 text-center">
        No results found.
      </div>;
  }
  return <div className={isRefreshing ? 'animate-pulse' : ''}>
      {displayProducts?.length > 0 && <AdvancedSearchProductsGrid products={displayProducts} />}
      {displayProducts?.length > 0 && !isLastPage && <div className="flex justify-center mt-8 mb-12">
          <LoadMoreButton onClick={showMore} isLoading={loading} />
        </div>}
    </div>;
};
export default AdvancedSearchProductHits;